import React, { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import {
  DeleteButton,
  FunctionButtonContainer,
  HorizontalContainer,
  ScreenRoot,
  StyledHeader,
  StyledTable,
  StyledTd,
  TableContainer,
  VerticalContainer,
} from "../styles";
import {
  FileUploadSection,
  FlexContainer,
  ProceedButtonContainer,
  SearchBarContainer,
  SearchUploadContainer,
  StageContainer,
  StageItem,
  StageLine,
  StageNumber,
  StyledTr,
  TitleContainer,
  UploadButton,
  UploadContainer,
  UploadTextHeader,
  UploadTextSubheader,
} from "./styles";
import { updateUnderwriterIdpDocumentCounter } from "api/external_data.js";
import { LargeText, MediumText } from "../../styles";
import { SearchBar } from "pages/contractorDashboard/detailScreens/SearchBar";
import { getUnderwriterClientInfo } from "../../../../data/actions/underwriters";
import { useDispatch, useSelector } from "react-redux";
import {
  underwriterClientSelector,
  underwriterIdpActiveDocumentsSelector,
} from "../../../../data/selectors/underwriters";
import { UploadIcon } from "../../../../assets/common/UploadIcon";
import axios from "axios";
import { API_HOST } from "../../../../constants";
import { AlertPopup } from "../../../../components/alertPopup/alertPopup";
import { FunctionalButton } from "../../../../components/functionalButton";
import { useHistory, useLocation } from "react-router";
import { ReactComponent as DeleteIcon } from "../../../../assets/common/deleteIcon.svg";
import { pdfjs } from "react-pdf";
import worker from "pdfjs-dist/build/pdf.worker.entry";
import { AccountRow } from "./accountRow";
import { contactSelector } from "data/selectors/contact";
import { getContact } from "data/actions/contact";
import { serviceProviderContractorsListSelector } from "data/selectors/serviceProvider";
import { getServiceProviderContractorsList } from "data/actions/serviceProvider";
import { getUnderwriterIdpActiveDocuments } from "../../../../data/actions/underwriters";

pdfjs.GlobalWorkerOptions.workerSrc = worker;

/**
 * Uploader is a stage in the **IDP Workflow**, it allows users to upload PDF files for an underwriter account.
 * Users can select an account, upload up to 3 files, and proceed to the next step in the process.
 *
 * @component
 * @returns {JSX.Element} The Uploader component.
 *
 * @example
 * return (
 *   <Uploader />
 * )
 */
export default function Uploader() {
  const isMobile = useMediaQuery({ maxWidth: 480 });
  const dispatch = useDispatch();
  const history = useHistory();

  const MAX_FILES_COUNT = 1;

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isAutomatedFlow = queryParams.get("automated") === "true";

  const fileRef = useRef();

  const [query, setQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [files, setFiles] = useState([]);
  const [showFileLimitPopup, setShowFileLimitPopup] = useState(false);
  const [showRemoveSelectedFilesPopup, setShowRemoveSelectedFilesPopup] =
    useState(false);

  const [clientDocumentsId, setClientDocumentsId] = useState(0);
  const [bucketName, setBucketName] = useState("");
  const [filePath, setFilePath] = useState("");

  const [activeDocuments, setActiveDocuments] = useState([]);

  const [
    showPasswordProtectedDocumentPopup,
    setShowPasswordProtectedDocumentPopup,
  ] = useState(false);

  const [loading, setLoading] = useState(false);

  const [showPendingDocumentPopup, setShowPendingDocumentPopup] =
    useState(false);

  const [selectedAccount, setSelectedAccount] = useState({
    AccountId: "",
    CompanyName: "",
    ClassifierThreshold: 0,
    documentsCount: 0,
    subsidiaries: null,
  });

  const [selectedSubsidiary, setSelectedSubsidiary] = useState({
    SubsidiaryID: "",
    SubsidiaryName: "",
    SubsidiaryAddress: "",
  });

  const contact = useSelector(contactSelector);

  useEffect(() => {
    if (!contact || !contact.user_id) {
      dispatch(getContact());
    }
  }, [dispatch, contact]);

  const isAccountSelected = !!selectedAccount.AccountId;
  const isSubsidiarySelected = !!selectedSubsidiary.SubsidiaryID;

  const hasMaxFiles = files.length >= 3;
  const hasPendingDocuments = isAccountSelected
    ? activeDocuments
        .filter((doc) => !doc.subsidiary_id)
        .map((doc) => doc.account_id)
        .includes(selectedAccount.AccountId)
    : activeDocuments
        .map((doc) => doc.subsidiary_id)
        .includes(selectedSubsidiary.SubsidiaryID);

  const hasValidSelection =
    (isAccountSelected && !isSubsidiarySelected) ||
    (!isAccountSelected && isSubsidiarySelected);

  const shouldDisableUpload = hasPendingDocuments;

  const underwriterClientInfoData = useSelector(
    underwriterClientSelector,
    (prev, next) => prev.length === next.length,
  );
  const serviceProviderContractorsData = useSelector(serviceProviderContractorsListSelector);

  const underwriterIdpActiveDocumentsData = useSelector(
    underwriterIdpActiveDocumentsSelector,
    (prev, next) => prev.length === next.length,
  );

  const user = JSON.parse(localStorage.getItem("User"));
  const isAgentUnderwriter = user?.is_agent_to_access_esub_and_idp ?? false;


  useEffect(() => {
    if (isAgentUnderwriter) {
      dispatch(getServiceProviderContractorsList());
    } else {
      dispatch(getUnderwriterClientInfo());
    }
  }, [isAgentUnderwriter, dispatch]);

  useEffect(() => {
    console.log(contact);
    if (contact?.user_id) {
      dispatch(getUnderwriterIdpActiveDocuments({ user_id: contact.user_id }));
    }
  }, [dispatch, contact]);

  useEffect(() => {
    if (isAgentUnderwriter) {
      // Handle e-sub data structure
      if (serviceProviderContractorsData?.data && serviceProviderContractorsData.data.length > 0) {
        console.log("Setting e-sub data from .data property:", serviceProviderContractorsData.data);
        setFilteredData(serviceProviderContractorsData.data);
      } else if (serviceProviderContractorsData && serviceProviderContractorsData.length > 0) {
        // Fallback in case data is directly in the array
        console.log("Setting e-sub data directly from array:", serviceProviderContractorsData);
        setFilteredData(serviceProviderContractorsData);
      } else {
        console.log("No e-sub data available, setting empty array");
        setFilteredData([]);
      }
    } else {
      // Handle regular underwriter data
      if (underwriterClientInfoData.length > 0) {
        console.log("Setting underwriter data:", underwriterClientInfoData);
        setFilteredData(underwriterClientInfoData);
      } else {
        console.log("No underwriter data available, setting empty array");
        setFilteredData([]);
      }
    }
    
    if (Array.isArray(underwriterIdpActiveDocumentsData.documents)) {
      console.log("hey: ", underwriterIdpActiveDocumentsData)
      setActiveDocuments(underwriterIdpActiveDocumentsData.documents);
    } else { 
      setActiveDocuments([]);
    }
  }, [isAgentUnderwriter, underwriterClientInfoData, serviceProviderContractorsData, underwriterIdpActiveDocumentsData]);

  useEffect(() => {
    if (location.state?.account_id && filteredData.length > 0) {
      const accountToSelect = filteredData.find(account => {
        if (isAgentUnderwriter) {
          return account.agent_account_id === location.state.account_id;
        } else {
          return account.AccountId === location.state.account_id;
        }
      });

      if (accountToSelect) {
        if (isAgentUnderwriter) {
          setSelectedAccount({
            AccountId: accountToSelect.agent_account_id,
            CompanyName: accountToSelect.institution_name,
            ClassifierThreshold: 0,
            documentsCount: 0,
            subsidiaries: null,
          });
        } else {
          setSelectedAccount({
            AccountId: accountToSelect.AccountId,
            CompanyName: accountToSelect.CompanyName,
            ClassifierThreshold: accountToSelect.ClassifierThreshold,
            documentsCount: accountToSelect.count,
            subsidiaries: accountToSelect.SubsidiaryAccountInfo,
          });
        }
      }
    }
  }, [location.state, filteredData, isAgentUnderwriter]);

  const handleOpenFile = () => {
    fileRef.current.click();
  };

  const handleFileChange = (event) => {
    const uploadedFiles = event.target.files;

    if (!uploadedFiles || uploadedFiles.length === 0) {
      console.log("No files selected or file explorer cancelled");
      return;
    }

    if (uploadedFiles.length > MAX_FILES_COUNT) {
      setShowFileLimitPopup(true);
      event.target.value = "";
    } else {
      const file = uploadedFiles[0];

      if (!file) {
        console.log("No file selected");
        return;
      }

      const checkIfPDFHasPassword = async (file) => {
        try {
          const fileText = await file.text();
          return fileText.includes("/Encrypt");
        } catch (error) {
          console.error("Error checking for PDF password:", error);
          return null;
        }
      };

      checkIfPDFHasPassword(file).then((isPasswordProtected) => {
        if (isPasswordProtected) {
          console.error("PDF is password protected");
          setShowPasswordProtectedDocumentPopup(true);
        } else {
          console.log(
            "File is not password-protected, adding directly to files.",
          );
          setFiles((files) => (files.length === 0 ? [file] : [...files, file]));
        }
      });
    }
  };

  const handleUploadPdfFiles = async (files, options = {}) => {
    const { isAutomatedFlow } = options;
    setLoading(true);
    let headers = {};
    const getAuth = () => {
      const authData = localStorage.getItem("Authorization");
      if (authData) {
        headers = { Authorization: authData };
      }
    };
    if (files && files.length > 0) {
      const formData = new FormData();

      formData.append("user_id", location.state?.userId || "");

      if (selectedAccount.AccountId && selectedAccount.CompanyName) {
        formData.append("account_id", selectedAccount.AccountId);
        formData.append("company_name", selectedAccount.CompanyName);
        formData.append("subsidiary_id", "");
      } else {
        formData.append("account_id", selectedSubsidiary.AccountId);
        formData.append("company_name", selectedSubsidiary.SubsidiaryName);
        formData.append("subsidiary_id", selectedSubsidiary.SubsidiaryID);
      }

      if (isAutomatedFlow) {
        formData.append("automatic", "Auto");
      }

      files.forEach((file) => {
        formData.append("pdfFile[]", file);
      });
      try {
        getAuth();

        // This API will respond with client_documents_id
        const response = await axios.post(
          `${API_HOST}/underwriter/idp/upload/files`,
          formData,
          {
            headers: {
              ...headers,
              "Content-Type": "multipart/form-data",
            },
          },
        );

        await updateUnderwriterIdpDocumentCounter(
          {},
          () => {
            console.log("Successfully updated the counter");
          },
          (error) => {
            console.log("Error updated counter: ", error);
          },
        );

        const { clientDocumentsId, bucketName, filePath } = response.data;

        setClientDocumentsId(clientDocumentsId);
        setBucketName(bucketName);
        setFilePath(filePath);

        return { clientDocumentsId, bucketName, filePath };
      } catch (error) {
        console.log("there was an error uploading the files ");
      } finally {
        setLoading(false);
      }
    }
  };

  console.log(
    "3 values after file upload ",
    clientDocumentsId,
    bucketName,
    filePath,
  );

  const handleProceed = async () => {
    if (!isAutomatedFlow) {
      const { clientDocumentsId, bucketName, filePath } =
        await handleUploadPdfFiles(files);

      history.push({
        pathname: "/underwriter/idpdocuments/splitter",
        state: {
          selectedAccount,
          selectedSubsidiary,
          clientDocumentsId,
          bucketName,
          filePath,
          userId: location.state?.userId,
        },
      });
    } else {
      const { clientDocumentsId, bucketName, filePath } = await handleUploadPdfFiles(files, { isAutomatedFlow: true });

      history.push({
        pathname: "/underwriter/idpdocuments?automated=true",
        state: {
          selectedAccount,
          selectedSubsidiary,
          clientDocumentsId,
          bucketName,
          filePath,
          userId: location.state?.userId,
        },
      });
    }
  };

  return (
    <>
      <AlertPopup
        headerText="Password Protected Document"
        height="434px"
        subHeaderText="The uploaded document is password protected. To proceed, please unlock it and try again."
        openPopup={showPasswordProtectedDocumentPopup}
        setOpenPopup={setShowPasswordProtectedDocumentPopup}
        showIcon={true}
        width={"640px"}
        showButton={false}
      />
      <AlertPopup
        headerText="IDP Pending For Client"
        height="434px"
        subHeaderText="Please remove pending document."
        openPopup={showPendingDocumentPopup}
        setOpenPopup={setShowPendingDocumentPopup}
        showIcon={true}
        width={"640px"}
        showButton={false}
      />
      <AlertPopup
        headerText="Remove Uploaded Document"
        height="434px"
        subHeaderText="Please remove uploaded document before selecting a new client."
        openPopup={showRemoveSelectedFilesPopup}
        setOpenPopup={setShowRemoveSelectedFilesPopup}
        showIcon={true}
        width={"640px"}
        showButton={false}
      />
      <AlertPopup
        headerText="Too many files selected"
        height="434px"
        subHeaderText={`Too many files selected. Maximum is ${MAX_FILES_COUNT}.`}
        openPopup={showFileLimitPopup}
        setOpenPopup={setShowFileLimitPopup}
        showIcon={true}
        width={"640px"}
        showButton={false}
      />
      <ScreenRoot isMobile={isMobile}>
        <VerticalContainer>
          <HorizontalContainer>
            <TitleContainer>
              <LargeText>Document Uploader</LargeText>
              <MediumText>Select the account for this document</MediumText>
            </TitleContainer>
            {!isAutomatedFlow ? (
              <StageList
                stages={["Uploader", "Splitter", "Classifier", "Parser"]}
                currentStage="Uploader"
              />
            ) : (
              <StageList stages={["Uploader"]} currentStage="Uploader" />
            )}
            <ProceedButtonContainer>
              <FunctionalButton
                buttonTitle={loading ? "Uploading..." : "Proceed"}
                handleButton={handleProceed}
                width="100%"
                height="48px"
                isReversedColor={false}
                marginRight={"48px"}
                marginLeft={"48px"}
                marginTop={"48px"}
                marginBottom={"48px"}
                fontSize={"16px"}
                disabled={files.length === 0 || loading}
                icon={null}
                hasBoxShadow={true}
                frontIcon={null}
                weight={600}
              />
            </ProceedButtonContainer>
          </HorizontalContainer>
          <SearchUploadContainer>
            <HorizontalContainer>
              <VerticalContainer>
                <SearchBarContainer>
                  <MediumText>Contractor Clients</MediumText>
                  <SearchBar
                    filteredData={filteredData}
                    setInternalSearchResults={setFilteredData}
                    query={query}
                    setQuery={setQuery}
                    internalSearch={true}
                    searchWidth="100%"
                    searchPlaceHolder="Search by Account Name or Account ID"
                  />
                </SearchBarContainer>
                <TableContainer>
                  <StyledTable>
                    <thead
                      style={{
                        position: "sticky",
                        top: 0,
                        background: "#fff",
                        zIndex: 1,
                      }}
                    >
                      <tr>
                        <StyledHeader style={{ width: "150px" }}>
                          Account ID
                        </StyledHeader>
                        <StyledHeader style={{ width: "400px" }}>
                          {isAgentUnderwriter ? "Company Name" : "Account/Sub Name"}
                        </StyledHeader>
                        <StyledHeader style={{ width: "100px" }}>
                          Docs Pending
                        </StyledHeader>
                        {isAgentUnderwriter && <StyledHeader>Subs</StyledHeader>}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        if (filteredData.length > 0) {
                          return filteredData.map((row, index) => {
                            if (isAgentUnderwriter) {
                              return (
                                <AccountRow
                                  key={row.id || index}
                                  AccountId={row.agent_account_id}
                                  CompanyName={row.institution_name}
                                  ClassifierThreshold={0}
                                  count={0}
                                  idp_doc_count={0}
                                  files={files}
                                  SubsidiaryAccountInfo={[]}
                                  activeDocuments={activeDocuments}
                                  setSelectedAccount={setSelectedAccount}
                                  selectedAccount={selectedAccount}
                                  selectedSubsidiary={selectedSubsidiary}
                                  setSelectedSubsidiary={setSelectedSubsidiary}
                                  setShowRemoveSelectedFilesPopup={
                                    setShowRemoveSelectedFilesPopup
                                  }
                                  setShowPendingDocumentPopup={
                                    setShowPendingDocumentPopup
                                  }
                                  isESub={true}
                                />
                              );
                            } else {
                              const {
                                AccountId,
                                CompanyName,
                                ClassifierThreshold,
                                count,
                                idp_doc_count,
                                SubsidiaryAccountInfo,
                              } = row;
                              return (
                                <AccountRow
                                  key={AccountId}
                                  AccountId={AccountId}
                                  CompanyName={CompanyName}
                                  ClassifierThreshold={ClassifierThreshold}
                                  count={count}
                                  idp_doc_count={idp_doc_count}
                                  files={files}
                                  SubsidiaryAccountInfo={SubsidiaryAccountInfo}
                                  activeDocuments={activeDocuments}
                                  setSelectedAccount={setSelectedAccount}
                                  selectedAccount={selectedAccount}
                                  selectedSubsidiary={selectedSubsidiary}
                                  setSelectedSubsidiary={setSelectedSubsidiary}
                                  setShowRemoveSelectedFilesPopup={
                                    setShowRemoveSelectedFilesPopup
                                  }
                                  setShowPendingDocumentPopup={
                                    setShowPendingDocumentPopup
                                  }
                                  isESub={false}
                                />
                              );
                            }
                          });
                        } else {
                          return (
                            <tr>
                              <td colSpan="4">No data available</td>
                            </tr>
                          );
                        }
                      })()}
                    </tbody>
                  </StyledTable>
                </TableContainer>
              </VerticalContainer>
              <FileUploadSection>
                <UploadContainer disabled={shouldDisableUpload}>
                  <UploadTextHeader>Upload Documents</UploadTextHeader>
                  <UploadTextSubheader>
                    Maximum {MAX_FILES_COUNT} file, 20MB (.PDF .TIFF .GIF .TIF)
                  </UploadTextSubheader>
                  <UploadTextSubheader style={{ marginBottom: "20px" }}>
                    To redact sensitive data, please use the{" "}
                    <span
                      style={{
                        color: "#007bff",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                      onClick={() => {
                        window.open("https://sit.pledgx.ai/redaction", "_blank");
                      }}
                    >
                      Redaction Tool
                    </span>
                    .
                  </UploadTextSubheader>
                  <UploadButton
                    disabled={shouldDisableUpload}
                    onClick={handleOpenFile}
                  >
                    <UploadIcon color={shouldDisableUpload} />
                    Choose File
                  </UploadButton>
                  <input
                    type="file"
                    ref={fileRef}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                    accept=".pdf,.gif,.tiff,.tif"
                    multiple
                  />
                </UploadContainer>
                {files.length > 0 && (
                  <StyledTable>
                    <thead>
                      <tr>
                        <StyledHeader style={{ width: "300px" }}>
                          Title
                        </StyledHeader>
                        <StyledHeader>Size</StyledHeader>
                      </tr>
                    </thead>
                    <tbody>
                      {files.length > 0 &&
                        files.map((file) => (
                          <StyledTr key={file.name}>
                            <StyledTd style={{ width: "300px" }}>
                              {file.name}
                            </StyledTd>
                            <StyledTd>
                              {(file.size / (1024 * 1024)).toFixed(2)} MB
                            </StyledTd>
                            <FunctionButtonContainer>
                              <DeleteButton
                                onClick={() => {
                                  const index = files.findIndex(
                                    (f) => f.name === file.name,
                                  );

                                  if (index !== -1) {
                                    const updatedFiles = [...files];
                                    updatedFiles.splice(index, 1);
                                    setFiles(updatedFiles);
                                    fileRef.current.value = "";
                                  }
                                }}
                              >
                                <DeleteIcon />
                              </DeleteButton>
                            </FunctionButtonContainer>
                          </StyledTr>
                        ))}
                    </tbody>
                  </StyledTable>
                )}
              </FileUploadSection>
            </HorizontalContainer>
          </SearchUploadContainer>
        </VerticalContainer>
      </ScreenRoot>
    </>
  );
}

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 10 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8.49373 0.729138C8.83831 0.384558 9.39698 0.384558 9.74156 0.729138C10.0861 1.07372 10.0861 1.63239 9.74156 1.97697L4.44745 7.27109C4.10287 7.61567 3.54419 7.61567 3.19961 7.27109L0.258435 4.32992C-0.0861451 3.98533 -0.0861451 3.42666 0.258435 3.08208C0.603015 2.7375 1.16169 2.7375 1.50627 3.08208L3.82353 5.39934L8.49373 0.729138Z"
      fill="white"
    />
  </svg>
);

/**
 * Takes an array of stages (array of strings) and a current stage (string) and creates a
 * wizard UI using them.
 *
 * @param {string[]} props.stages - An array of stage names.
 * @param {string} props.currentStage - The current stage name.
 * @returns {JSX.Element}
 * @throws Error Will throw an error if the currentStage is not included in the stages
 array.
 */
export const StageList = ({ stages, currentStage }) => {
  if (!stages.includes(currentStage)) {
    throw new Error("currentStage must be included in stages");
  }

  return (
    <FlexContainer>
      {stages.map((stage) => (
        <StageContainer key={stage} class="dd">
          <HorizontalContainer>
            <StageLine
              background={
                stages.indexOf(stage) <= stages.indexOf(currentStage)
                  ? "#FF6D1D"
                  : "#DCDCE1"
              }
            />
            <StageItem
              background={
                stages.indexOf(stage) <= stages.indexOf(currentStage)
                  ? "#FF6D1D"
                  : "#DCDCE1"
              }
            >
              {stage === currentStage ? (
                <StageNumber>{stages.indexOf(stage) + 1}</StageNumber>
              ) : stages.indexOf(stage) < stages.indexOf(currentStage) ? (
                <CheckIcon />
              ) : (
                <StageNumber>{stages.indexOf(stage) + 1}</StageNumber>
              )}
            </StageItem>
            <StageLine
              background={
                stages.indexOf(stage) < stages.indexOf(currentStage)
                  ? "#FF6D1D"
                  : "#DCDCE1"
              }
            />
          </HorizontalContainer>
          <p style={{ textAlign: "center" }}>
            {stage}
          </p>
        </StageContainer>
      ))}
    </FlexContainer>
  );
};
